import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { SITE } from "@/lib/site";

const LOGO_SRC = "https://customer-assets.emergentagent.com/job_monarch-style/artifacts/2795r72c_IMG_3956.jpeg";

const links = [
  { to: "/", label: "Home" },
  { to: "/barbers", label: "Barbers" },
  { to: "/shop", label: "Shop" },
  { to: "/journal", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-monarch-black/95 backdrop-blur-xl border-b border-monarch-gold/20 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.8)]"
          : "bg-monarch-black/60 backdrop-blur-md border-b border-monarch-gold/10"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <img
            src={LOGO_SRC}
            alt="Monarch Cutz"
            className="h-12 md:h-14 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.28em] font-semibold transition-colors duration-300 ${
                  isActive ? "text-monarch-gold" : "text-monarch-cream/80 hover:text-monarch-gold"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={SITE.phoneHref}
            data-testid="navbar-call-link"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-monarch-cream/80 hover:text-monarch-gold transition-colors"
          >
            <Phone className="w-3.5 h-3.5" />
            {SITE.phone}
          </a>
          <Link to="/book" data-testid="navbar-book-button" className="btn-gold !py-3 !px-6">
            Book a Chair
          </Link>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          className="lg:hidden text-monarch-cream p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="navbar-mobile-menu" className="lg:hidden bg-monarch-black border-t border-monarch-gold/15">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.28em] font-semibold ${
                    isActive ? "text-monarch-gold" : "text-monarch-cream/85"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/book" data-testid="mobile-book-button" className="btn-gold mt-2 justify-center">
              Book a Chair
            </Link>
            <a href={SITE.phoneHref} data-testid="mobile-call-link" className="btn-outline-gold justify-center">
              <Phone className="w-4 h-4" /> Call {SITE.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
